import { Params, redirect, replace } from 'react-router-dom';
import { ROUTES } from '@constants';
import { useApplicationStore } from '@data';
import { getTimeZone, logLead } from '@utils';
import {
  getAvailabilitiesForPatient,
  loadScheduleCachedPromise,
  loadServiceTypesByState,
  searchNextAvailability,
  VisitTypeIds,
} from './utils';
import { Availabilities, VisitType } from '@types';
import { DRUG_SCREEN_ROUTE_PATHNAME } from '@router/routes/routePathnames';
import {
  enrollInMatExperimentIfQualifies,
  getIntakeQuizRedirectPath,
} from '@lib/experiments/matQuestions';
import { ensureFeatureFlagsFetched } from '@lib/firebase';
import { Events, tracking } from '@lib/events';

export type AvailabilitiesPromise = Promise<Availabilities | null>;

let cachedPromise: AvailabilitiesPromise | null; // Stores the last fetched data

// We only want to track when the visittype changes
let trackedVisitTypeId = '';
const MHTRedirectUrl =
  'https://quick.md/treatment/waitlist/menopause-hormone-therapy/';
export interface AvailabilitiesLoader {
  availabilities: AvailabilitiesPromise;
}

export default async function scheduleLoader({
  params,
  request,
}: {
  params: Params<string>;
  request: Request;
}): Promise<AvailabilitiesLoader | Response> {
  await ensureFeatureFlagsFetched();

  const { visitTypeId } = params;
  const {
    getVisitTypeById,
    setUtmParameters,
    setStateCode,
    stateCode,
    utmParameters,
    serviceTypes,
    patient,
    setVisitTypeId,
    flags,
  } = useApplicationStore.getState();

  const url = new URL(request.url);
  const date = url.searchParams.get('date');
  const providerId = url.searchParams.get('providerId');
  const searchState = stateCode || patient?.patientProfile?.address.stateCode;
  const data = await loadScheduleCachedPromise(cachedPromise, date, providerId);

  if (data) {
    return {
      availabilities: new Promise(function (resolve) {
        resolve(data);
      }),
    };
  }
  setVisitTypeId(visitTypeId || null);
  logLead();
  if (!serviceTypes.length && searchState) {
    await loadServiceTypesByState(searchState);
  }

  const visitType = getVisitTypeById(visitTypeId || '') as VisitType | null;

  if (
    !visitType ||
    !visitTypeId ||
    !searchState ||
    (!date && !patient?.patientProfile)
  ) {
    setUtmParameters({ ...utmParameters, service: null });
    return redirect(
      `${ROUTES.services.path}${searchState ? `?state=${searchState}` : ''}`,
    );
  }

  // Treatment-arm MAT patients who reach /schedule via a deep link or
  // history navigation (not the services-page click) would otherwise
  // bypass the intake quiz entirely. Send them to the quiz first so
  // Experiment Started, the question views, and the answers all line up
  // in the same session — and `enrollInMatExperimentIfQualifies` fires
  // from the quiz page (after BE confirms a session) instead of from here.
  const intakeQuizPath = getIntakeQuizRedirectPath(visitTypeId);
  if (intakeQuizPath) {
    return redirect(intakeQuizPath);
  }

  // Enroll in the MAT-questions experiment when the user reaches the
  // scheduling funnel for an addiction-treatment visit type, regardless of
  // whether they got here via the services-page click or a direct deep link.
  // The helper is gated on addiction-treatment + experiment kill switch +
  // anonymous, and is idempotent within the session.
  enrollInMatExperimentIfQualifies(visitTypeId);

  if (
    visitTypeId === VisitTypeIds.MenopauseHormoneTherapy &&
    !flags.mhtStates.states.find((value) => value === searchState)
  ) {
    tracking.trackEvent(Events.REDIRECT_TO_MHT_WAIT_LIST, {
      state: searchState,
    });

    return redirect(MHTRedirectUrl);
  }

  if (visitType.isDrugScreenRequired) {
    return replace(`${DRUG_SCREEN_ROUTE_PATHNAME}?visitTypeId=${visitTypeId}`);
  }

  setStateCode(searchState);
  const timeZone = getTimeZone();

  try {
    const fetchAvailabilities: AvailabilitiesPromise = patient?.patientProfile
      ? getAvailabilitiesForPatient({
          providerId,
          date,
          timeZone,
          visitTypeId,
          trackAvailability: visitTypeId !== trackedVisitTypeId,
        })
      : searchNextAvailability({
          state: searchState,
          date: date!,
          providerId,
          timeZone,
          visitTypeId,
          trackAvailability: visitTypeId !== trackedVisitTypeId,
        });

    if (!date || providerId) {
      cachedPromise = fetchAvailabilities;
    } else {
      cachedPromise = null;
    }

    trackedVisitTypeId = visitTypeId;

    return { availabilities: fetchAvailabilities };
  } catch (error) {
    // @TODO: Handle error
    console.error('Error fetching availabilities:', error);
    // In case we got a bad service parameter, clear it out
    setUtmParameters({ ...utmParameters, service: null });
    return redirect(ROUTES.services.path);
  }
}
